import { createContext, useState, useCallback, useEffect } from 'react'

import { refreshThemeEventDispatch } from '@space-lib-events/refreshTheme'

import { usePersistedStateCookie } from '@space-lib/hooks'
import { light, dark } from '@space-lib/styles/themes'

import { ThemeMode } from '@space-lib/styles/themes/enum'
import type { ThemeType } from '@space-lib/styles/themes/types'
import { ThemeProvider } from 'styled-components'

import { STORAGE_THEME_KEY } from './constants'
import type { ThemeContextProps, ThemeWrapperProps } from './types'

const defaultContext: ThemeContextProps = {
  switchTheme: () => undefined,
  theme: ThemeMode.DARK
}

export const ThemeContext = createContext(defaultContext)

export function ThemeWrapper({ children, creatorTheme, allowChangeTheme }: ThemeWrapperProps) {
  const [storedTheme, setStoredTheme] = usePersistedStateCookie<ThemeMode>(
    STORAGE_THEME_KEY,
    creatorTheme || ThemeMode.DARK
  )
  const [theme, setTheme] = useState<ThemeType>(storedTheme === ThemeMode.LIGHT ? light : dark)
  useEffect(() => {
    if (!allowChangeTheme) {
      setTheme(creatorTheme === ThemeMode.LIGHT ? light : dark)
      setStoredTheme(creatorTheme || ThemeMode.DARK)
      refreshThemeEventDispatch(creatorTheme || ThemeMode.DARK)
    }
  }, [allowChangeTheme, creatorTheme, theme, setStoredTheme])

  const toggleTheme = useCallback(() => {
    if (storedTheme === ThemeMode.DARK) {
      setTheme(light)
      setStoredTheme(ThemeMode.LIGHT)
      refreshThemeEventDispatch(ThemeMode.LIGHT)
    } else {
      setTheme(dark)
      setStoredTheme(ThemeMode.DARK)
      refreshThemeEventDispatch(ThemeMode.DARK)
    }
  }, [setStoredTheme, storedTheme])

  return (
    <ThemeContext.Provider
      value={{
        switchTheme: toggleTheme,
        theme: storedTheme
      }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  )
}
